import { Component, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { ContextService } from '../services/context.service';
import { DatabaseService, Patient } from '../services/db.service';
import { FormsModule } from '@angular/forms';
import { ToastService } from '../services/toast.service';
import { LoadingService } from '../services/loading.service';
import { LEGAL_TEXTS } from '../constants/legal-content';
import { ThemeService } from '../services/theme.service';
import { SecurityUtil } from '../utils/security.util';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'app-public-search',
    standalone: true,
    imports: [CommonModule, FormsModule],
    providers: [DatePipe],
    template: `
    <div class="min-h-screen bg-slate-50 dark:bg-slate-950 relative font-sans p-4 md:p-12 selection:bg-brand-main/10 transition-colors duration-300">
        <!-- Background Elements -->
        <div class="absolute inset-0 overflow-hidden pointer-events-none">
            <div class="absolute top-[-5%] right-[-5%] w-[500px] h-[500px] bg-brand-main/10 rounded-full blur-[120px]"></div>
            <div class="absolute bottom-[-5%] left-[-5%] w-[500px] h-[500px] bg-teal-500/10 rounded-full blur-[120px]"></div>
        </div>

        <div class="max-w-3xl mx-auto relative z-10">
            <!-- Premium Control Pill -->
            @if (!isDirectPortalAccess()) {
                <div class="flex justify-center mb-10 animate-fade-in pt-4">
                    <div class="flex items-center gap-2 p-1.5 bg-white/10 dark:bg-slate-900/40 backdrop-blur-xl border border-slate-200/50 dark:border-slate-800/50 rounded-2xl shadow-xl shadow-black/5 transition-all duration-300 hover:shadow-2xl hover:shadow-black/10">
                        <button (click)="context.isPublicStandalone.set(false)" 
                                class="flex items-center gap-2.5 px-4 py-2 hover:bg-white/10 dark:hover:bg-slate-800/50 rounded-xl transition-all group/back">
                            <svg class="w-4 h-4 text-slate-400 group-hover/back:-translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
                            </svg>
                            <span class="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">Sair do Portal</span>
                        </button>
                        
                        <div class="w-px h-4 bg-slate-200/20 dark:bg-slate-800/50 mx-1"></div>
                        
                        <button (click)="themeService.toggleDarkMode()" 
                                class="w-9 h-9 flex items-center justify-center hover:bg-white/10 dark:hover:bg-slate-800/50 rounded-xl text-slate-400 dark:text-slate-500 hover:text-brand-main transition-all"
                                title="Alternar Tema">
                            @if (!themeService.isDarkMode()) {
                                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"></path></svg>
                            } @else {
                                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"></path></svg>
                            }
                        </button>
                    </div>
                </div>
            } @else {
                <div class="flex justify-end mb-10 animate-fade-in pt-4">
                    <button (click)="themeService.toggleDarkMode()" 
                            class="w-9 h-9 flex items-center justify-center hover:bg-white/10 dark:hover:bg-slate-800/50 rounded-xl text-slate-400 dark:text-slate-500 hover:text-brand-main transition-all"
                            title="Alternar Tema">
                        @if (!themeService.isDarkMode()) {
                            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"></path></svg>
                        } @else {
                            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"></path></svg>
                        }
                    </button>
                </div>
            }

            <!-- Header/Branding -->
            <div class="flex flex-col items-center mb-6 text-center animate-fade-in relative">

                <div class="w-14 h-14 bg-white dark:bg-slate-900 rounded-[24px] flex items-center justify-center shadow-xl shadow-slate-200/50 dark:shadow-none p-3 border border-slate-100 dark:border-slate-800 mb-4 transition-transform hover:scale-105 duration-500">
                    <img src="assets/images/logo-icon.svg" alt="Logo FilaSaúde" class="w-full h-full object-contain">
                </div>
                <div>
                    <h1 class="text-3xl font-black text-slate-800 dark:text-slate-100 tracking-tight mb-1">Fila<span class="text-brand-main">Saúde</span></h1>
                    <p class="text-[9px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-[0.4em] opacity-80 leading-none">Portal da Transparência</p>
                </div>
            </div>

            <div class="bg-white dark:bg-slate-900 rounded-[32px] md:rounded-[40px] shadow-[0_20px_60px_-15px_rgba(0,0,0,0.05)] border border-slate-100 dark:border-slate-800 overflow-hidden animate-enter transition-all duration-500">
                <div class="p-5 md:p-10 border-b border-slate-50 dark:border-slate-800 bg-gradient-to-b from-slate-50/30 dark:from-slate-800/30 to-white dark:to-slate-900">
                    <div class="flex items-center gap-5 text-left">
                        <div class="w-14 h-14 shrink-0 rounded-[20px] bg-brand-main flex items-center justify-center shadow-xl shadow-brand-main/20 text-white">
                            <svg class="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
                            </svg>
                        </div>
                        <div>
                            <h3 class="text-lg font-black text-slate-900 dark:text-slate-100 tracking-tight">Buscar atendimento</h3>
                            <p class="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest leading-tight">Consulte seu status na fila oficial</p>
                        </div>
                    </div>
                </div>

                <div class="p-5 md:p-10">
                    <!-- Input Section -->
                    <div class="space-y-3 mb-5">
                        <div class="relative group">
                            <label class="block text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest ml-1 mb-1.5 transition-colors group-focus-within:text-brand-main">CPF do Cidadão</label>
                            <div class="relative">
                                <input [(ngModel)]="globalSearchInput"
                                    (input)="onCpfInput($event)"
                                    maxlength="14"
                                    placeholder="000.000.000-00" 
                                    class="w-full pl-12 pr-6 py-3.5 bg-slate-50 dark:bg-slate-800 border-2 border-transparent rounded-[20px] focus:bg-white dark:focus:bg-slate-900 focus:ring-8 focus:ring-brand-main/5 focus:border-brand-main outline-none transition-all font-black text-base text-slate-800 dark:text-slate-100 shadow-sm placeholder:text-slate-300 dark:placeholder:text-slate-600">
                                <div class="absolute left-4 top-1/2 -translate-y-1/2 flex items-center justify-center w-5 h-5 text-slate-300 group-focus-within:text-brand-main transition-colors">
                                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path></svg>
                                </div>
                            </div>
                        </div>

                        <div class="relative group">
                            <label class="block text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest ml-1 mb-1.5 transition-colors group-focus-within:text-brand-main">Data de Nascimento</label>
                            <div class="relative flex gap-2">
                                <input type="text" 
                                    [ngModel]="dayInput"
                                    (ngModelChange)="onDayChange($event)"
                                    maxlength="2"
                                    placeholder="DD"
                                    class="w-14 py-3.5 bg-slate-50 dark:bg-slate-800 border-2 border-transparent rounded-[20px] focus:bg-white dark:focus:bg-slate-900 focus:ring-4 focus:ring-brand-main/5 focus:border-brand-main outline-none transition-all font-black text-center text-slate-800 dark:text-slate-100 shadow-sm placeholder:text-slate-300 dark:placeholder:text-slate-600">
                                <input type="text" 
                                    [ngModel]="monthInput"
                                    (ngModelChange)="onMonthChange($event)"
                                    maxlength="2"
                                    placeholder="MM"
                                    class="w-14 py-3.5 bg-slate-50 dark:bg-slate-800 border-2 border-transparent rounded-[20px] focus:bg-white dark:focus:bg-slate-900 focus:ring-4 focus:ring-brand-main/5 focus:border-brand-main outline-none transition-all font-black text-center text-slate-800 dark:text-slate-100 shadow-sm placeholder:text-slate-300 dark:placeholder:text-slate-600">
                                <input type="text" 
                                    [ngModel]="yearInput"
                                    (ngModelChange)="onYearChange($event)"
                                    maxlength="4"
                                    placeholder="AAAA"
                                    class="w-20 py-3.5 bg-slate-50 dark:bg-slate-800 border-2 border-transparent rounded-[20px] focus:bg-white dark:focus:bg-slate-900 focus:ring-4 focus:ring-brand-main/5 focus:border-brand-main outline-none transition-all font-black text-center text-slate-800 dark:text-slate-100 shadow-sm placeholder:text-slate-300 dark:placeholder:text-slate-600">
                            </div>
                        </div>
                    </div>

                    <!-- LGPD Consent -->
                    <div class="mb-5 flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-800 rounded-[20px] border border-slate-100 dark:border-slate-800 transition-all hover:bg-slate-100/50 dark:hover:bg-slate-700/50 shadow-inner group/consent">
                        <div class="relative flex items-center shrink-0">
                            <input type="checkbox" [(ngModel)]="acceptedTerms" id="lgpd-consent" 
                                class="peer h-5 w-5 cursor-pointer appearance-none rounded-2xl border-2 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 transition-all checked:bg-brand-main checked:border-brand-main outline-none hover:border-brand-main shadow-sm focus:ring-4 focus:ring-brand-main/10">
                            <svg class="absolute h-3 w-3 text-white opacity-0 peer-checked:opacity-100 transition-all scale-50 peer-checked:scale-100 pointer-events-none left-1 top-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="4" d="M5 13l4 4L19 7"></path>
                            </svg>
                        </div>
                        <label for="lgpd-consent" class="text-[10px] text-slate-500 dark:text-slate-400 leading-tight cursor-pointer select-none font-bold group-hover/consent:text-slate-700 dark:group-hover/consent:text-slate-300 transition-colors">
                            Autorizo o uso dos dados para transparência pública, conforme 
                            <button (click)="openLegal(legalTexts.PRIVACY_POLICY)" class="text-brand-main hover:underline font-black">Política</button> 
                            e 
                            <button (click)="openLegal(legalTexts.TERMS_OF_USE)" class="text-brand-main hover:underline font-black">Termos</button>.
                        </label>
                    </div>

                    <!-- Action Button -->
                    <button (click)="onSearchPatients()" 
                            [disabled]="!acceptedTerms() || globalSearchInput.length < 14 || searchBirthDate().length < 10 || isSearching()"
                            class="w-full py-5 bg-slate-900 border-b-4 border-black text-white rounded-[24px] text-sm font-black uppercase tracking-wider shadow-xl hover:bg-brand-main hover:border-brand-main/80 hover:-translate-y-1 active:translate-y-0 active:border-b-0 transition-all disabled:opacity-30 disabled:cursor-not-allowed disabled:translate-y-0 disabled:border-b-0 group relative overflow-hidden">
                            <div class="absolute inset-0 bg-white/10 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000 skew-x-12"></div>
                            <span class="relative flex items-center justify-center gap-4">
                                {{ isSearching() ? 'Processando...' : 'Consultar Portal' }}
                                @if (!isSearching()) {
                                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M14 5l7 7m0 0l-7 7m7-7H3"></path></svg>
                                }
                            </span>
                    </button>

                    <!-- Results Area - Conditional Spacing -->
                    <div [class.mt-8]="isSearching() || searchResults().length > 0 || hasSearched()" class="space-y-6 animate-fade-in">
                        @if (searchResults().length > 0 || hasSearched()) {
                            <div class="flex justify-start mb-4">
                                <button (click)="resetSearchResults()" 
                                        class="flex items-center gap-2 text-xs font-black text-brand-main uppercase tracking-widest hover:opacity-80 transition-all group/back">
                                    <svg class="w-4 h-4 transition-transform group-hover/back:-translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
                                    </svg>
                                    Voltar / Nova Consulta
                                </button>
                            </div>
                        }

                        @if (isSearching()) {
                            <div class="flex flex-col items-center justify-center py-12">
                                <div class="w-14 h-14 border-[5px] border-slate-100 dark:border-slate-800 border-t-brand-main rounded-full animate-spin"></div>
                                <p class="text-[10px] font-black text-slate-400 dark:text-slate-500 mt-6 uppercase tracking-[0.3em] animate-pulse">Sincronizando portal...</p>
                            </div>
                        } @else if (searchResults().length > 0) {
                            <div class="space-y-6">
                                <div class="flex items-center gap-3 px-1">
                                    <div class="w-2.5 h-2.5 rounded-full bg-emerald-500 shadow-lg shadow-emerald-500/20"></div>
                                    <span class="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-[0.2em]">Registros Localizados ({{ searchResults().length }})</span>
                                </div>
                                @for (p of searchResults(); track p.id) {
                                    <div class="group relative p-6 md:p-8 rounded-[32px] border-2 border-slate-50 dark:border-slate-800 bg-white dark:bg-slate-900 hover:border-brand-main/20 hover:shadow-2xl hover:shadow-brand-main/10 transition-all duration-500">
                                        <div class="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-8">
                                            <div class="flex items-center gap-4">
                                                <div class="w-16 h-16 rounded-[24px] bg-slate-50 dark:bg-slate-800 flex items-center justify-center text-slate-300 dark:text-slate-600 group-hover:bg-brand-main group-hover:text-white transition-all shadow-inner group-hover:rotate-6">
                                                    <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path></svg>
                                                </div>
                                                <div>
                                                    <h4 class="font-black text-slate-900 dark:text-slate-100 text-xl uppercase tracking-tight mb-1">{{ p.socialName || p.name }}</h4>
                                                    <p class="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest flex items-center gap-2">
                                                        <span class="w-1.5 h-1.5 rounded-full bg-brand-main"></span>
                                                        Identidade Validada
                                                    </p>
                                                </div>
                                            </div>
                                            
                                            <div class="px-6 py-3 rounded-2xl bg-slate-50 dark:bg-slate-800 border border-slate-100 dark:border-slate-700 text-center w-full md:w-auto">
                                                <span class="text-[9px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest block mb-1">Município</span>
                                                <span class="text-sm font-black text-slate-800 dark:text-slate-200">{{ getMunicipalityName(p.municipalityId) }}</span>
                                            </div>
                                        </div>

                                        <button (click)="autoLoginAsPatient(p)" class="w-full py-5 bg-brand-main border-b-4 border-brand-main/30 text-white rounded-[24px] text-xs font-black uppercase tracking-[0.2em] hover:bg-slate-900 hover:border-black hover:-translate-y-1 transition-all shadow-xl active:translate-y-0 active:border-b-0 flex items-center justify-center gap-3 group/btn">
                                            Acessar Painel 
                                            <svg class="w-4 h-4 transition-transform group-hover/btn:translate-x-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M14 5l7 7m0 0l-7 7m7-7H3"></path></svg>
                                        </button>
                                    </div>
                                }
                            </div>
                        } @else if (hasSearched()) {
                            <div class="text-center py-12 bg-slate-50 dark:bg-slate-800 rounded-[40px] border-4 border-dashed border-slate-100 dark:border-slate-800 px-8">
                                <div class="w-16 h-16 bg-white dark:bg-slate-900 rounded-[28px] mx-auto flex items-center justify-center shadow-xl dark:shadow-none mb-6 text-slate-200 dark:text-slate-700">
                                    <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                                </div>
                                <h4 class="text-xl font-black text-slate-800 dark:text-slate-100 uppercase tracking-tight mb-2">Registro não localizado</h4>
                                <p class="text-sm font-bold text-slate-400 dark:text-slate-500 leading-relaxed max-w-sm mx-auto">Não encontramos atendimentos ativos para os dados informados.</p>
                                <div class="mt-6 flex items-center justify-center gap-3">
                                    <div class="h-px w-6 bg-slate-200 dark:bg-slate-800"></div>
                                    <p class="text-[9px] font-black text-brand-main uppercase tracking-widest">Verifique seus documentos</p>
                                    <div class="h-px w-6 bg-slate-200 dark:bg-slate-800"></div>
                                </div>
                            </div>
                        }
                    </div>
                </div>
            </div>

            <footer class="mt-8 text-center space-y-4">
                <div class="flex items-center justify-center gap-2">
                    <div class="h-px w-12 bg-slate-200 dark:bg-slate-800"></div>
                    <img src="assets/images/logo-icon.svg" class="w-5 h-5 grayscale opacity-20" alt="Icon">
                    <div class="h-px w-12 bg-slate-200 dark:bg-slate-800"></div>
                </div>
                <p class="text-xs text-slate-500 dark:text-slate-600 font-medium pb-4">
                    © 2026 FilaSaúde <span class="mx-2 text-slate-300 dark:text-slate-800">•</span> Transparência e Cidadania
                </p>
            </footer>
        </div>

        <!-- Legal Content Modal Overlay -->
        @if (showLegalContent()) {
            <div class="fixed inset-0 z-[200] flex items-center justify-center p-6">
                <div class="absolute inset-0 bg-black/80 backdrop-blur-md animate-fade-in" (click)="showLegalContent.set(false)"></div>
                <div class="relative bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-[48px] shadow-[0_40px_100px_-20px_rgba(0,0,0,0.5)] w-full max-w-2xl overflow-hidden flex flex-col max-h-[85vh] animate-enter">
                    <div class="px-12 py-10 border-b border-slate-50 dark:border-slate-800 flex justify-between items-center bg-slate-50/50 dark:bg-slate-800/50">
                        <div class="flex items-center gap-6">
                            <div class="w-14 h-14 rounded-2xl bg-slate-900 dark:bg-brand-main flex items-center justify-center text-white shadow-xl">
                                <svg class="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path></svg>
                            </div>
                            <h3 class="text-2xl font-black text-slate-900 dark:text-slate-100 tracking-tight">Legal</h3>
                        </div>
                        <button (click)="showLegalContent.set(false)" class="p-4 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-3xl transition-all active:scale-90 shadow-sm border border-slate-50 dark:border-slate-800"><svg class="w-6 h-6 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path></svg></button>
                    </div>
                    <div class="flex-grow overflow-y-auto custom-scrollbar bg-white dark:bg-slate-900 p-12">
                        <div class="whitespace-pre-wrap text-sm text-slate-500 dark:text-slate-400 font-bold leading-loose" [innerHTML]="legalModalContent()"></div>
                    </div>
                    <div class="px-12 py-8 border-t border-slate-50 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/50 flex justify-end">
                        <button (click)="showLegalContent.set(false)" class="px-12 py-5 bg-slate-900 dark:bg-brand-main text-white rounded-[24px] text-xs font-black uppercase tracking-[0.3em] shadow-xl hover:bg-brand-main dark:hover:bg-brand-main/80 transition-all active:scale-95">Fechar Documento</button>
                    </div>
                </div>
            </div>
        }
    </div>
    `
})
export class PublicSearchComponent {
    context = inject(ContextService);
    db = inject(DatabaseService);
    toast = inject(ToastService);
    loading = inject(LoadingService);
    themeService = inject(ThemeService);
    legalTexts = LEGAL_TEXTS;

    showLegalContent = signal(false);
    legalModalContent = signal('');
    globalSearchInput = '';
    searchResults = signal<Patient[]>([]);
    isSearching = signal(false);
    hasSearched = signal(false);
    searchBirthDate = signal('');
    acceptedTerms = signal(false);
    dayInput = '';
    monthInput = '';
    yearInput = '';

    resetSearchResults() {
        this.searchResults.set([]);
        this.hasSearched.set(false);
        this.isSearching.set(false);
        this.globalSearchInput = '';
        this.searchBirthDate.set('');
        this.acceptedTerms.set(false);
    }

    isDirectPortalAccess(): boolean {
        return window.location.pathname.includes('/portal') || 
               new URLSearchParams(window.location.search).get('portal') === 'public';
    }

    onCpfInput(event: Event) {
        const input = event.target as HTMLInputElement;
        let value = input.value.replace(/\D/g, '');
        if (value.length > 11) value = value.slice(0, 11);
        if (value.length > 9) value = value.replace(/^(\d{3})(\d{3})(\d{3})(\d{1,2})/, '$1.$2.$3-$4');
        else if (value.length > 6) value = value.replace(/^(\d{3})(\d{3})(\d{1,3})/, '$1.$2.$3');
        else if (value.length > 3) value = value.replace(/^(\d{3})(\d{1,3})/, '$1.$2');
        this.globalSearchInput = value;
    }

    onDayChange(value: string) {
        const nums = value.replace(/\D/g, '').slice(0, 2);
        if (nums === this.dayInput) return;
        this.dayInput = nums;
        if (nums.length === 2) {
            let d = parseInt(nums) || 0;
            if (d > 31) this.dayInput = '31';
        }
        this.compileBirthDate();
    }

    onMonthChange(value: string) {
        const nums = value.replace(/\D/g, '').slice(0, 2);
        if (nums === this.monthInput) return;
        this.monthInput = nums;
        if (nums.length === 2) {
            let m = parseInt(nums) || 0;
            if (m > 12) this.monthInput = '12';
        }
        this.compileBirthDate();
    }

    onYearChange(value: string) {
        const nums = value.replace(/\D/g, '').slice(0, 4);
        if (nums === this.yearInput) return;
        this.yearInput = nums;
        this.compileBirthDate();
    }

    private compileBirthDate() {
        if (this.dayInput && this.monthInput && this.yearInput) {
            const d = parseInt(this.dayInput) || 0;
            const m = parseInt(this.monthInput) || 0;
            const y = parseInt(this.yearInput) || 0;
            if (d >= 1 && d <= 31 && m >= 1 && m <= 12 && y >= 1900) {
                this.searchBirthDate.set(`${y}-${this.monthInput}-${this.dayInput}`);
            } else {
                this.searchBirthDate.set('');
            }
        }
    }

    private validateCpf(cpf: string): boolean {
        const nums = cpf.replace(/\D/g, '');
        if (nums.length !== 11 || /^(\d)\1{10}$/.test(nums)) return false;
        let sum = 0, rest;
        for (let i = 1; i <= 9; i++) sum += parseInt(nums.charAt(i - 1)) * (11 - i);
        rest = (sum * 10) % 11;
        if (rest !== parseInt(nums.charAt(9))) return false;
        sum = 0;
        for (let i = 1; i <= 10; i++) sum += parseInt(nums.charAt(i - 1)) * (12 - i);
        rest = (sum * 10) % 11;
        return rest === parseInt(nums.charAt(10));
    }

    async autoLoginAsPatient(patient: Patient) {
        this.context.currentPatientCpf.set(patient.cpf);
        this.context.currentPatientBirthDate.set(patient.birthDate);
        await this.context.login('PACIENTE', patient.municipalityId);
        this.context.isPublicStandalone.set(false);
    }

    async onSearchPatients() {
        if (!this.globalSearchInput || this.globalSearchInput.length < 14) return;

        // Proteção contra injeção SQL em busca pública
        if (SecurityUtil.isPotentialSqlInjection(this.globalSearchInput) || SecurityUtil.isPotentialSqlInjection(this.searchBirthDate())) {
            this.toast.show('Padrão de busca inválido. Ação registrada por motivos de segurança.', 'error');
            return;
        }

        if (!this.validateCpf(this.globalSearchInput)) {
            this.toast.show('CPF inválido. Verifique os dados informados.', 'warning');
            return;
        }
        if (!this.searchBirthDate()) {
            this.toast.show('Informe a data de nascimento para verificar sua identidade.', 'info');
            return;
        }
        if (!this.acceptedTerms()) {
            this.toast.show('Aceite os termos para prosseguir.', 'info');
            return;
        }
        this.isSearching.set(true);
        this.hasSearched.set(true);
        try {
            const res: any = await this.db.searchPatientPublic(this.globalSearchInput, this.searchBirthDate());
            const results = Array.isArray(res) ? res : [res];
            this.searchResults.set(results);
        } catch (err) {
            console.error('Erro na pesquisa global:', err);
        } finally {
            this.isSearching.set(false);
        }
    }

    getMunicipalityName(id: string) {
        return this.context.availableMunicipalities().find(m => String(m.id).trim().toUpperCase() === String(id).trim().toUpperCase())?.name || id;
    }

    openLegal(content: string) {
        this.legalModalContent.set(content);
        this.showLegalContent.set(true);
    }
}

